import type { CSSProperties, HTMLAttributes, KeyboardEventHandler, PointerEventHandler, Ref, RefCallback } from 'react'

import {
    DND_DRAGGABLE_ATTR,
    DND_DROP_ID_ATTR,
    DND_DROPPABLE_ATTR,
    DND_KEYBOARD_INSTRUCTIONS_ID,
    type DndEngineHandlers,
    notifyDraggableKeyDown,
    notifyDraggablePointerDown,
} from '../engine'
import { composeRefs, handlerRef } from './refs'

// Untyped React layer under createDnd. Everything here is a pure prop builder
// with zero effects: the engine owns all gesture listeners, so items only stamp
// attributes, forward the initial press, and (for droppables with their own
// handlers) register those handlers from a ref callback at commit time.

export type DndDragArgs = {
    kitId: string
    payload: unknown
}

export type DndDropArgs = {
    kitId: string
    id?: string
    /** Omitted when the droppable relies on its zone or kit handlers; then no ref is attached. */
    handlers?: DndEngineHandlers
}

export type DndElementProps = HTMLAttributes<HTMLElement> & { ref?: RefCallback<HTMLElement> }

// React's HTMLAttributes carries no data-* index signature, so the engine's
// contract attributes get a minimal targeted supplement instead of a cast.
type DndDataAttributes = {
    [DND_DRAGGABLE_ATTR]?: string
    [DND_DROPPABLE_ATTR]?: string
    [DND_DROP_ID_ATTR]?: string
}

// Props read off a cloned child to chain handlers, keep explicit a11y props,
// merge styles, and compose refs. Kept minimal so any single-element child typechecks.
export type ClonableChildProps = {
    onPointerDown?: PointerEventHandler<HTMLElement>
    onKeyDown?: KeyboardEventHandler<HTMLElement>
    tabIndex?: number
    role?: string
    'aria-describedby'?: string
    style?: CSSProperties
    ref?: Ref<HTMLElement>
}

export function draggableProps(drag: DndDragArgs, child: ClonableChildProps): Omit<ClonableChildProps, 'ref'> & DndDataAttributes {
    const { kitId, payload } = drag
    // The child's own press behavior (e.g. focus, selection) runs first; the
    // drag notification follows.
    const onPointerDown: PointerEventHandler<HTMLElement> = event => {
        child.onPointerDown?.(event)
        notifyDraggablePointerDown({ kitId, payload, event: event.nativeEvent, sourceElement: event.currentTarget })
    }
    // Keyboard lift mirrors the pointer path; the engine filters for Space/Enter plus the busy-guard.
    const onKeyDown: KeyboardEventHandler<HTMLElement> = event => {
        child.onKeyDown?.(event)
        notifyDraggableKeyDown({ kitId, payload, event: event.nativeEvent, sourceElement: event.currentTarget })
    }
    return {
        [DND_DRAGGABLE_ATTR]: kitId,
        onPointerDown,
        onKeyDown,
        // tabIndex makes the item reachable; role + describedby keep the lift
        // discoverable (the instructions node is engine-owned, created when the
        // kit registers). Explicit child values win.
        tabIndex: child.tabIndex ?? 0,
        role: child.role ?? 'button',
        'aria-describedby': child['aria-describedby'] ?? DND_KEYBOARD_INSTRUCTIONS_ID,
        // The engine cannot preventDefault the touch-scroll gesture from its
        // window listeners; this declaration keeps a press from scrolling instead of dragging.
        style: { ...child.style, touchAction: 'none' },
    }
}

export function droppableProps(drop: DndDropArgs, child: ClonableChildProps): DndDataAttributes & { ref?: RefCallback<HTMLElement> } {
    const props: DndDataAttributes & { ref?: RefCallback<HTMLElement> } = { [DND_DROPPABLE_ATTR]: drop.kitId }
    if (drop.id !== undefined) props[DND_DROP_ID_ATTR] = drop.id
    if (drop.handlers !== undefined) props.ref = composeRefs(handlerRef(drop.handlers), child.ref)
    return props
}

/** Props to spread onto a draggable element. Returns no props when disabled. */
export function buildDraggableProps(drag: DndDragArgs & { disabled?: boolean }): DndElementProps {
    if (drag.disabled === true) return {}
    return draggableProps(drag, {})
}

/** Props to spread onto a droppable element. Returns no props when disabled. */
export function buildDroppableProps(drop: DndDropArgs & { disabled?: boolean }): DndElementProps {
    if (drop.disabled === true) return {}
    return droppableProps(drop, {})
}
